import React, { useState, useEffect, useRef } from "react";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Button } from "primereact/button";
import { Avatar } from "primereact/avatar";
import { EssCard } from "./components/helper";
import { PRIORITY_OPT } from "./data/constants";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUser } from "../../services/store/userSlice";
import { supportApi } from "../../services";

const MODULE_OPT = ["Leaves", "Attendance", "Payroll", "Expenses"].map((v) => ({
  label: v,
  value: v,
}));

const SLA_COLORS = {
  Critical: "var(--red)",
  High: "var(--orange)",
  Medium: "var(--blue2)",
  Low: "var(--green)",
};

const NewTicket = () => {
  const navigate = useNavigate();
  const currentUser = useSelector(selectUser);
  const [form, setForm] = useState({
    categoryId: null,
    subcategoryId: null,
    subject: "",
    priority: "Low",
    watchers: [],
    description: "",
  });
  const [categories, setCategories] = useState([]);
  const categoriesRef = useRef([]);
  const [subCategories, setSubCategories] = useState([]);
  const [subCatLoading, setSubCatLoading] = useState(false);
  const [assignPreview, setAssignPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [slaConfig, setSlaConfig] = useState(null);
  const [slaLoading, setSlaLoading] = useState(false);
  const [slaDeptLabel, setSlaDeptLabel] = useState("");
  const [userOptions, setUserOptions] = useState([]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  // On mount: load categories and active users
  useEffect(() => {
    Promise.all([
      supportApi.getCategories(),
      supportApi.getActiveUsers(),
    ]).then(([catRes, userRes]) => {
        const cats = Array.isArray(catRes.data?.data) ? catRes.data.data : [];
        const mappedCats = cats.map((c) => ({
          label: c.name,
          value: c.id,
          departmentId: c.departmentId,
          departmentTitle: c.department?.title ?? "",
          description: c.description ?? "",
        }));
        categoriesRef.current = mappedCats;
        setCategories(mappedCats);

        const users = Array.isArray(userRes.data?.data) ? userRes.data.data : [];
        setUserOptions(users
          .filter((u) => u.id !== currentUser?.id)
          .map((u) => ({
            label: `${u.firstName} ${u.lastName}`.trim(),
            value: u.id,
            email: u.email,
          }))
        );
      })
      .catch(() => {});
  }, []); // eslint-disable-line

  const onCategoryChange = async (categoryId) => {
    set("categoryId", categoryId);
    set("subcategoryId", null);
    setSubCategories([]);
    setAssignPreview(null);

    // Always wipe SLA state first — null means "loading", guarantees no old dept data bleeds through
    setSlaConfig(null);
    setSlaDeptLabel("");

    if (!categoryId) return;

    setSubCatLoading(true);
    setPreviewLoading(true);
    setSlaLoading(true);

    try {
      const [subRes, assignRes, slaRes] = await Promise.allSettled([
        supportApi.getSubCategories(categoryId),
        supportApi.previewAssignment(categoryId),
        supportApi.getSlaConfigByCategory(categoryId),
      ]);

      if (subRes.status === "fulfilled") {
        const subs = Array.isArray(subRes.value.data?.data) ? subRes.value.data.data : [];
        setSubCategories(subs.map((s) => ({ label: s.name, value: s.id })));
      }

      if (assignRes.status === "fulfilled") {
        const d = assignRes.value.data?.data;
        setAssignPreview(d?.userId ? d : null);
      }

      if (slaRes.status === "fulfilled") {
        // Always set a new array reference so React detects the state change
        const rows = Array.isArray(slaRes.value.data?.data) ? slaRes.value.data.data : [];
        setSlaConfig([...rows]); // spread ensures new reference even when rows is []
        const matched = categoriesRef.current.find((c) => c.value === categoryId);
        setSlaDeptLabel(matched?.departmentTitle ?? "");
      } else {
        // API failed — still clear to empty so no old data shows
        setSlaConfig([]);
      }
    } catch {}
    finally {
      setSubCatLoading(false);
      setPreviewLoading(false);
      setSlaLoading(false);
    }
  };

  const onFileChange = (e) => {
    const files = Array.from(e.target.files);
    setAttachments((p) => [...p, ...files]);
    e.target.value = "";
  };

  const onSubmit = async () => {
    if (
      !form.categoryId ||
      !form.subject?.trim() ||
      !form.description?.trim()
    ) {
      toast.warn("Please fill all required fields");
      return;
    }
    setSubmitting(true);
    try {
      const res = await supportApi.createTicket(form);
      const ticketData = res.data?.data ?? res.data;
      const ticketId = ticketData?.id;
      if (attachments.length && ticketId) {
        await supportApi.uploadAttachment(ticketId, attachments);
        setUploadedFiles(attachments.map((f) => f.name));
      }
      // const agentName = ticketData?.assignedAgent
      //   ? `${ticketData.assignedAgent.firstName} ${ticketData.assignedAgent.lastName}`.trim()
      //   : assignPreview?.agentName;
      toast.success(
        // agentName
        //   ? `Ticket submitted & auto-assigned to ${agentName}`
        //   :
         "Ticket submitted successfully",
      );
      navigate(`/support/ticket-details/${ticketId}`);
    } catch {
      toast.error("Failed to submit ticket");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <h1 className="ess-page-title">Raise New Support Ticket</h1>
      <p className="ess-page-sub">
        Fill in the details below. Our support team will respond within 24
        hours.
      </p>

      <div
        style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: 20 }}
      >
        <EssCard title="Ticket Information" icon="pi-file-edit">
          {form.categoryId && (() => {
            const cat = categoriesRef.current.find((c) => c.value === form.categoryId);
            return cat?.description ? (
              <div style={{ display: "flex", alignItems: "flex-start", gap: 8, background: "var(--blue-light, #e8f4fd)", border: "1px solid var(--blue2, #4a90d9)", borderRadius: 6, padding: "8px 12px", marginBottom: 14, fontSize: 12, color: "var(--blue2, #4a90d9)" }}>
                <i className="pi pi-info-circle" style={{ marginTop: 1, flexShrink: 0 }} />
                <span>{cat.description}</span>
              </div>
            ) : null;
          })()}
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}
          >
            <div className="p-field">
              <label
                style={{
                  fontSize: 11.5,
                  fontWeight: 600,
                  display: "block",
                  marginBottom: 5,
                }}
              >
                Category <span style={{ color: "var(--red)" }}>*</span>
              </label>
              <Dropdown
                value={form.categoryId}
                options={categories}
                onChange={(e) => onCategoryChange(e.value)}
                placeholder="Select category…"
                style={{ width: "100%" }}
                filter
                filterPlaceholder="Search category…"
              />
              {previewLoading && (
                <div
                  style={{
                    marginTop: 6,
                    fontSize: 11.5,
                    color: "var(--muted)",
                  }}
                >
                  <i
                    className="pi pi-spin pi-spinner"
                    style={{ marginRight: 5 }}
                  />
                  Checking assignment rule…
                </div>
              )}
              {!previewLoading && assignPreview && (
                <div
                  style={{
                    marginTop: 6,
                    fontSize: 11.5,
                    color: "var(--green)",
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                  }}
                >
                  {/* <i className="pi pi-user-edit" />
                  Will be auto-assigned to:{" "}
                  <strong>{assignPreview.agentName}</strong>
                  <span
                    style={{
                      fontSize: 10,
                      color: "var(--muted)",
                      marginLeft: 4,
                    }}
                  >
                    (
                    {assignPreview.ruleType === "round_robin"
                      ? "Round Robin"
                      : "Direct"}
                    )
                  </span> */}
                </div>
              )}
              {/* {!previewLoading && form.categoryId && !assignPreview && (
                <div
                  style={{
                    marginTop: 6,
                    fontSize: 11.5,
                    color: "var(--orange)",
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                  }}
                >
                  <i className="pi pi-exclamation-triangle" />
                  No auto-assignment rule — ticket will be unassigned
                </div>
              )} */}
            </div>
            <div className="p-field">
              <label
                style={{
                  fontSize: 11.5,
                  fontWeight: 600,
                  display: "block",
                  marginBottom: 5,
                }}
              >
                Sub-Category
              </label>
              <Dropdown
                value={form.subcategoryId}
                options={subCategories}
                onChange={(e) => set("subcategoryId", e.value)}
                placeholder={subCatLoading ? "Loading…" : subCategories.length ? "Select sub-category…" : "No sub-categories"}
                disabled={!form.categoryId || subCatLoading}
                style={{ width: "100%" }}
                filter
                filterPlaceholder="Search sub-category…"
              />
            </div>
            <div className="p-field" style={{ gridColumn: "1 / -1" }}>
              <label
                style={{
                  fontSize: 11.5,
                  fontWeight: 600,
                  display: "block",
                  marginBottom: 5,
                }}
              >
                Subject <span style={{ color: "var(--red)" }}>*</span>
              </label>
              <InputText
                value={form.subject}
                onChange={(e) => set("subject", e.target.value)}
                placeholder="Brief description of your issue…"
                style={{ width: "100%" }}
              />
            </div>
            <div className="p-field">
              <label
                style={{
                  fontSize: 11.5,
                  fontWeight: 600,
                  display: "block",
                  marginBottom: 5,
                }}
              >
                Priority
              </label>
              <Dropdown
                value={form.priority}
                options={PRIORITY_OPT}
                onChange={(e) => set("priority", e.value)}
                placeholder="Select priority…"
                style={{ width: "100%" }}
                filter
                filterPlaceholder="Search priority…"
              />
            </div>
            <div className="p-field">
              <label
                style={{
                  fontSize: 11.5,
                  fontWeight: 600,
                  display: "block",
                  marginBottom: 5,
                }}
              >
                Watchers
              </label>
              <MultiSelect
                value={form.watchers}
                options={userOptions}
                onChange={(e) => set("watchers", e.value)}
                optionLabel="label"
                optionValue="value"
                placeholder="Add watchers…"
                filter
                filterPlaceholder="Search users…"
                maxSelectedLabels={0}
                selectedItemsLabel={form.watchers.length === 1 ? "1 watcher selected" : `${form.watchers.length} watchers selected`}
                style={{ width: "100%" }}
                itemTemplate={(opt) => (
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Avatar
                      label={opt.label.substring(0, 2).toUpperCase()}
                      shape="circle"
                      size="small"
                      style={{ background: "var(--blue)", color: "#fff", fontSize: 10, width: 26, height: 26 }}
                    />
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 600 }}>{opt.label}</div>
                      <div style={{ fontSize: 10, color: "var(--muted)" }}>{opt.email}</div>
                    </div>
                  </div>
                )}
              />
            </div>
            {/* <div className="p-field">
              <label
                style={{
                  fontSize: 11.5,
                  fontWeight: 600,
                  display: "block",
                  marginBottom: 5,
                }}
              >
                Related Module
              </label>
              <Dropdown
                value={form.relatedModule}
                options={MODULE_OPT}
                onChange={(e) => set("relatedModule", e.value)}
                placeholder="Select module…"
                style={{ width: "100%" }}
              />
            </div> */}
            <div className="p-field" style={{ gridColumn: "1 / -1" }}>
              <label
                style={{
                  fontSize: 11.5,
                  fontWeight: 600,
                  display: "block",
                  marginBottom: 5,
                }}
              >
                Description <span style={{ color: "var(--red)" }}>*</span>
              </label>
              <InputTextarea
                value={form.description}
                onChange={(e) => set("description", e.target.value)}
                rows={5}
                style={{ width: "100%" }}
                placeholder="Describe your issue in detail. Include steps to reproduce, error messages, screenshots, etc."
              />
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label
                style={{
                  fontSize: 11.5,
                  fontWeight: 600,
                  display: "block",
                  marginBottom: 5,
                }}
              >
                Attachments
              </label>
              <div
                className="upload-zone"
                onClick={() => document.getElementById("nt-file-input").click()}
              >
                <input
                  id="nt-file-input"
                  type="file"
                  multiple
                  accept=".png,.jpg,.jpeg,.pdf"
                  onChange={onFileChange}
                  style={{ display: "none" }}
                />
                <i
                  className="pi pi-upload"
                  style={{
                    fontSize: 22,
                    color: "var(--blue2)",
                    display: "block",
                    marginBottom: 8,
                  }}
                />
                Drag & drop files here, or{" "}
                <strong style={{ color: "var(--blue)" }}>browse</strong>
                <br />
                <span style={{ fontSize: 11 }}>PNG, JPG, PDF up to 10 MB</span>
              </div>
              {attachments.length > 0 && (
                <div
                  style={{
                    marginTop: 8,
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 6,
                  }}
                >
                  {attachments.map((f, i) => (
                    <div
                      key={i}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                        background: "var(--light)",
                        border: "1px solid var(--border)",
                        borderRadius: 4,
                        padding: "2px 8px",
                        fontSize: 11,
                      }}
                    >
                      <i
                        className="pi pi-file"
                        style={{ fontSize: 11, color: "var(--blue)" }}
                      />
                      <span>{f.name}</span>
                      <i
                        className="pi pi-times"
                        style={{
                          fontSize: 10,
                          color: "var(--red)",
                          cursor: "pointer",
                          marginLeft: 2,
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setAttachments((p) =>
                            p.filter((_, idx) => idx !== i),
                          );
                        }}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div
            style={{
              display: "flex",
              gap: 10,
              justifyContent: "flex-end",
              marginTop: 20,
            }}
          >
            <Button
              label="Cancel"
              outlined
              severity="secondary"
              onClick={() => navigate("/support")}
            />
            <Button
              label="Submit Ticket"
              icon="pi pi-send"
              loading={submitting}
              style={{ background: "var(--blue)", border: "none" }}
              onClick={onSubmit}
            />
          </div>
        </EssCard>

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <EssCard title={`SLA Response Times${slaDeptLabel ? ` — ${slaDeptLabel}` : ""}`} icon="pi-clock">
            {slaLoading ? (
              <div style={{ color: "var(--muted)", fontSize: 12, padding: "8px 0" }}>
                <i className="pi pi-spin pi-spinner" style={{ marginRight: 6 }} />
                Loading SLA times…
              </div>
            ) : slaConfig === null ? (
              // Not yet loaded — no category selected
              <div style={{ color: "var(--muted)", fontSize: 12, padding: "4px 0" }}>
                Select a category to view SLA response times.
              </div>
            ) : slaConfig.length === 0 ? (
              // Loaded but empty — no SLA configured for this dept
              <div style={{ color: "var(--muted)", fontSize: 12, padding: "4px 0" }}>
                No SLA configured for this department.
              </div>
            ) : (
              // Loaded with data — show only this dept's rows
              (() => {
                const maxHrs = Math.max(...slaConfig.map((r) => r.firstResponseHrs || 0), 1);
                return slaConfig.map((r) => (
                  <div key={r.priority} style={{ marginBottom: 12 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: SLA_COLORS[r.priority] }}>
                        {r.priority}
                      </span>
                      <span style={{ fontSize: 11, color: "var(--muted)" }}>
                        First response: <strong>{r.firstResponseHrs}h</strong>
                        {r.resolutionHrs ? ` · Resolve: ${r.resolutionHrs}h` : ""}
                      </span>
                    </div>
                    <div style={{ background: "#e9ecef", borderRadius: 4, height: 8, width: "100%" }}>
                      <div
                        style={{
                          width: `${Math.round((r.firstResponseHrs / maxHrs) * 100)}%`,
                          height: "100%",
                          borderRadius: 4,
                          background: SLA_COLORS[r.priority],
                          transition: "width 0.4s ease",
                        }}
                      />
                    </div>
                  </div>
                ));
              })()
            )}
          </EssCard>
          <EssCard title="Before You Raise a Ticket" icon="pi-info-circle">
            {[
              "Check the Knowledge Base for quick answers",
              "Include screenshots where possible",
              "Mention exact error messages",
              "Select the correct priority level",
            ].map((tip, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  gap: 8,
                  fontSize: 12,
                  color: "var(--muted)",
                  marginBottom: 8,
                }}
              >
                <i
                  className="pi pi-check-circle"
                  style={{ color: "var(--green)", marginTop: 1 }}
                />
                {tip}
              </div>
            ))}
          </EssCard>
        </div>
      </div>
    </div>
  );
};

export default NewTicket;
